import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuthStore, useAuthHydrated } from '@/stores/auth-store';
import { useSettingsStore, useSettingsHydrated } from '@/stores/settings-store';
import { usePrepTestStore } from '@/stores/prep-test-store';
import { useNotifications } from '@/hooks/useNotifications';
import { SWEDISH_REGIONS, getRegionFromPostalCode } from '@/data/regions';
import PrepTestModal from '@/components/PrepTestModal';
import LogoutConfirmModal from '@/components/LogoutConfirmModal';
import ConfirmationModal from '@/components/ConfirmationModal';
import PremiumRequiredModal from '@/components/PremiumRequiredModal';
import ModalBackdrop from '@/components/ModalBackdrop';
import {
  User,
  Bell,
  Globe,
  Target,
  Users, 
  CreditCard, 
  Crown,
  LogOut,
  Trash2,
  Plus,
  X,
  Mail,
  Check,
  MapPin,
  Lock,
  ClipboardCheck,
  RefreshCw,
  Settings,
  Download,
  Upload,
} from 'lucide-react';
import useToastStore from '@/stores/toast-store';
import { useFamilyStore } from '@/stores/family-store';
import { exportProfile, importProfile, downloadProfileExport, readProfileExport } from '@/lib/profile-export';
import { getNativePlatform } from '@/utils/native-platform';

const FORBIDDEN_DISPLAY_NAMES = ['beredskapshubben', 'admin', 'administratör'];
const ANDROID_PREMIUM_PRODUCT_ID = 'premium';
const ANDROID_PREMIUM_OFFER_ID = 'premium-monthly';
const IOS_PREMIUM_PRODUCT_ID = 'premium.monthly';

type ReactNativeWebViewBridge = {
  postMessage: (message: string) => void;
};

type PaddleCheckoutOptions = {
  items: Array<{ priceId: string; quantity: number }>;
  customer: { email?: string };
  customData: string;
  settings: { displayMode: 'overlay' };
};

type PaddleEvent = {
  name: string;
};

type PaddleBridge = {
  Environment: { set: (environment: string) => void };
  Initialize: (options: { token: string; eventCallback: (event: PaddleEvent) => void }) => void;
  Checkout: {
    open: (options: PaddleCheckoutOptions) => void;
    close?: () => void;
  };
};

type SettingsWindow = Window & {
  ReactNativeWebView?: ReactNativeWebViewBridge;
  Paddle?: PaddleBridge;
  _paddleInitialized?: boolean;
  __pendingImportFile?: File;
  nativePlatform?: string;
};

export default function SettingsPage() {
  const appWindow = window as SettingsWindow;
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, logoutLocal, isAuthenticated, updateUser, fetchUser } = useAuthStore();
  const { disablePremium } = useAuthStore();
  const authHydrated = useAuthHydrated();
  const settingsHydrated = useSettingsHydrated();
  const {
    vmaRegion, 
    vmaPostalCode, 
    setVmaRegion,
    notifications,
    setNotification,
    fetchFromServer,
    familyMembers,
    addFamilyMember,
    removeFamilyMember,
    updateFamilyMember,
    userDailyCalories,
    setUserDailyCalories,
  } = useSettingsStore();
  const { hasCompletedTest, testTakenAt, overallScore, resetTest } = usePrepTestStore();
  const hasTest = hasCompletedTest();
  const family = useFamilyStore((s) => s.family);
  const isFamilyOwner = useFamilyStore((s) => s.isOwner);
  const isMemberReadOnly = !!family && !isFamilyOwner;
  const pushToast = useToastStore((s) => s.push);
  const [showPrepTest, setShowPrepTest] = useState(false);
  
  // Notification permissions
  const { permissionStatus, requestPermission } = useNotifications();
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  
  // Hämta inställningar från server vid mount (vänta på BÅDA hydrations)
  useEffect(() => {
    if (authHydrated && settingsHydrated && isAuthenticated) {
      (async () => {
        // Hämta inställningar och användare från servern
        await fetchFromServer();
        await fetchUser(); // Hämta senaste user-data från servern

        // Note: do not forcibly reset `prepGoal` here. Downgrade handling
        // is already implemented reactively in the settings-store/auth-store
        // subscription logic to avoid overwriting a server value when simply
        // opening Settings on another device.
      })();
    }
  // fetchFromServer and fetchUser are stable store actions; we intentionally only run this when auth & settings hydrate.
   
  }, [authHydrated, settingsHydrated, isAuthenticated, fetchFromServer, fetchUser]);
  
  // Scroll to hash element on mount and when hash changes
  useEffect(() => {
    if (!location.hash) return;

    // Auto-expand plan details on desktop when arriving via #get-premium
    if (location.hash === '#get-premium' && window.innerWidth >= 1024) {
      setShowFreePlanDetails(true);
      setShowPremiumPlanDetails(true);
    }

    const scrollToHash = () => {
      const element = document.querySelector(location.hash);
      if (!element) return;

      const isMobile = window.innerWidth < 768;
      const yOffset = isMobile ? -80 : -50; // kompensera för sticky header
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    };

    // Försök flera gånger för att hantera timing/race conditions
    scrollToHash();
    const timer1 = window.setTimeout(scrollToHash, 200);
    const timer2 = window.setTimeout(scrollToHash, 600);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [location.hash, location.pathname]);
  
  // Local state (would be synced with backend)
  const [displayName, setDisplayName] = useState((user?.displayName || '').substring(0, 50));
  const [displayNameChanged, setDisplayNameChanged] = useState(false);
  // If user is a family member, show the owner's householdDisplayName as read-only
  const ownerHousehold = family?.ownerHouseholdDisplayName as string | undefined;
  const [householdDisplayName, setHouseholdDisplayName] = useState((ownerHousehold ?? user?.settings?.householdDisplayName) || '');
  const [householdDisplayNameChanged, setHouseholdDisplayNameChanged] = useState(false);
  const [email, _setEmail] = useState(user?.email || '');
  const { prepGoal, setPrepGoal } = useSettingsStore((s) => ({ prepGoal: s.prepGoal, setPrepGoal: s.setPrepGoal }));
  const [language, setLanguage] = useState(i18n.language || 'sv');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [confirmDisablePremiumOpen, setConfirmDisablePremiumOpen] = useState(false);
  const [premiumManagementInfoStore, setPremiumManagementInfoStore] = useState<'apple' | 'google' | 'web' | null>(null);
  const [pendingGoal, setPendingGoal] = useState<"7d" | "30d" | "90d" | null>(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const appleOwnershipCheckUserIdRef = useRef<string | null>(null);
  const [premiumFeature, setPremiumFeature] = useState('Beredskapsmål för 30 eller 90 dagar');
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<'adult' | 'child' | 'pet'>('adult');
  const [newMemberCalories, setNewMemberCalories] = useState<number>(2000);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [showPremiumInfoModal, setShowPremiumInfoModal] = useState(false);
  const [premiumActivationStatus, setPremiumActivationStatus] = useState<'idle' | 'polling' | 'success' | 'failed'>('idle');
  const nativePlatform = getNativePlatform();
  const subscriptionStoreType = user?.storeType ?? (user?.paddleSubscriptionId ? 'web' : undefined);
  const canDirectlyDisablePremium = user?.subscriptionTier === 'premium' && !subscriptionStoreType;
  const showGooglePlayManagement = subscriptionStoreType === 'google';
  const showAppStoreManagement = subscriptionStoreType === 'apple';
  const showWebSubscriptionManagement = subscriptionStoreType === 'web';
  const canOpenPaddlePortal = showWebSubscriptionManagement && !appWindow.ReactNativeWebView && !!user?.paddleSubscriptionId;
  const premiumManagementInfoTitle = premiumManagementInfoStore === 'google'
    ? 'Hantera Premium i Google Play'
    : premiumManagementInfoStore === 'apple'
      ? 'Hantera Premium i Apple App Store'
      : premiumManagementInfoStore === 'web'
        ? 'Hantera Premium på webben'
        : '';
  const premiumManagementInfoDescription = premiumManagementInfoStore === 'google'
    ? 'Din Premium-prenumeration för Android hanteras av Google Play. För att avsluta den öppnar du Google Play Butik och går till Betalningar och prenumerationer → Prenumerationer, och väljer sedan Beredskapshubben.'
    : premiumManagementInfoStore === 'apple'
      ? 'Din Premium-prenumeration för iPhone hanteras av Apple App Store. För att avsluta den öppnar du App Store, trycker på din profilbild, går till Prenumerationer och väljer sedan Beredskapshubben.'
      : premiumManagementInfoStore === 'web'
        ? 'Din Premium-prenumeration för webben hanteras i webbläsaren. Öppna Beredskapshubben på webben och gå till Inställningar → Premium för att hantera eller avsluta prenumerationen där.'
        : '';
  
  // Export/Import state
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showExportInfo, setShowExportInfo] = useState(false);
  const [showFreePlanDetails, setShowFreePlanDetails] = useState(false);
  const [showPremiumPlanDetails, setShowPremiumPlanDetails] = useState(false);
  
  // Synka displayName när user uppdateras (t.ex. från server eller efter sparning)
  useEffect(() => {
    if (user?.displayName) {
      setDisplayName(user.displayName.substring(0, 50));
      setDisplayNameChanged(false);
    }
  }, [user?.displayName]);
  
  // Synka householdDisplayName när user uppdateras
  useEffect(() => {
    // If we are in family mode and the owner provides a household name, prefer that
    const owner = useFamilyStore.getState().family?.ownerHouseholdDisplayName as string | undefined;
    if (owner) {
      setHouseholdDisplayName(owner);
      setHouseholdDisplayNameChanged(false);
      return;
    }
    if (user?.settings?.householdDisplayName !== undefined) {
      setHouseholdDisplayName(user.settings.householdDisplayName);
      setHouseholdDisplayNameChanged(false);
    }
  }, [user?.settings?.householdDisplayName]);
  
  // VMA Region - local state for input, synced to store
  const [postalCode, setPostalCode] = useState(vmaPostalCode);
  const [postalCodeError, setPostalCodeError] = useState('');
  
  // Synka postalCode när store uppdateras (t.ex. från server)
  useEffect(() => {
    setPostalCode(vmaPostalCode);
  }, [vmaPostalCode]);

  const startNativePremiumPurchase = useCallback(() => {
    const reactNativeWebView = appWindow.ReactNativeWebView;

    if (!nativePlatform || !reactNativeWebView) {
      return false;
    }

    if (!user?.id) {
      pushToast({
        title: 'Du måste vara inloggad',
        description: 'Logga in igen innan du startar ett köp i appen.',
        variant: 'error',
      });
      return true;
    }

    const productId = nativePlatform === 'ios'
      ? IOS_PREMIUM_PRODUCT_ID
      : ANDROID_PREMIUM_PRODUCT_ID;

    reactNativeWebView.postMessage(JSON.stringify({
      type: 'INITIATE_PURCHASE',
      productId,
      offerId: nativePlatform === 'android' ? ANDROID_PREMIUM_OFFER_ID : undefined,
      userId: user.id,
    }));

    return true;
  }, [appWindow.ReactNativeWebView, nativePlatform, pushToast, user?.id]);

  // Hantera köpresultat från native-appen (Android/iOS)
  useEffect(() => {
    const notifyVerifiedPurchase = () => {
      appWindow.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'PURCHASE_VERIFIED' }));
    };

    const handleGooglePlayPurchase = async (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const { purchaseToken, productId } = detail;
      try {
        const { apiPost } = await import('@/services/api-client');
        const result = await apiPost<{ success: boolean }>('/premium/verify-android-purchase', { purchaseToken, productId });
        if (!result.success) throw new Error('Verifiering misslyckades');
        await fetchUser();
        notifyVerifiedPurchase();
        pushToast({ title: 'Välkommen till Premium! 🎉', description: 'Din prenumeration är nu aktiv.', variant: 'success' });
      } catch {
        pushToast({ title: 'Något gick fel', description: 'Försök igen om en stund.', variant: 'error' });
      }
    };

    const handleAppStorePurchase = async (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const { environment, isOwnershipCheck, originalTransactionId, productId, shouldFinishTransaction, transactionId } = detail;
      try {
        const { apiPost } = await import('@/services/api-client');
        const result = await apiPost<{ success: boolean }>('/premium/verify-apple-purchase', {
          transactionId,
          originalTransactionId,
          productId,
          environment,
        });
        if (!result.success) throw new Error('Verifiering misslyckades');
        await fetchUser();
        if (!isOwnershipCheck || shouldFinishTransaction) {
          notifyVerifiedPurchase();
        }
        pushToast({
          title: isOwnershipCheck
            ? t('settings.premium.appleOwnershipFoundTitle', 'Premium hittades')
            : 'Välkommen till Premium! 🎉',
          description: t('settings.premium.appleOwnershipFoundMessage', 'Din prenumeration är nu aktiv.'),
          variant: 'success',
        });
      } catch {
        if (isOwnershipCheck) {
          appleOwnershipCheckUserIdRef.current = null;
        }
        pushToast({ title: 'Något gick fel', description: 'App Store-köpet kunde inte verifieras just nu.', variant: 'error' });
      }
    };

    const handleApplePremiumCheckResult = (event: Event) => {
      const detail = (event as CustomEvent).detail as {
        found: boolean;
        error?: boolean;
        initiatedByPurchase?: boolean;
      };
      if (!detail.error && (detail.found || !detail.initiatedByPurchase)) return;

      appleOwnershipCheckUserIdRef.current = null;
      pushToast({
        title: detail.error
          ? t('settings.premium.appleOwnershipCheckErrorTitle', 'Kunde inte kontrollera App Store-köpet')
          : t('settings.premium.appleOwnershipNotFoundTitle', 'Ingen aktiv prenumeration hittades'),
        description: detail.error
          ? t('settings.premium.appleOwnershipCheckErrorMessage', 'Kontrollera internetanslutningen och försök igen.')
          : t('settings.premium.appleOwnershipNotFoundMessage', 'Apple rapporterade att produkten redan ägs, men ingen aktiv prenumeration kunde verifieras. Kontrollera Apple-kontot eller kontakta supporten.'),
        variant: 'error',
      });
    };

    const handleGooglePlayPurchaseError = (event: Event) => {
      const detail = (event as CustomEvent).detail as { cancelled: boolean; code?: string; message?: string; [key: string]: unknown };
      const { cancelled, code, message } = detail;
      if (cancelled) return;

      const baseMessage = message?.trim() || 'Tekniskt fel vid köp i Google Play.';
      const codeLabel = code || 'okänd';
      const debugDetails = !code && !message ? ` Detaljer: ${JSON.stringify(detail)}` : '';
      const description = `${baseMessage} (kod: ${codeLabel})${debugDetails}`;

      pushToast({ title: 'Köpet misslyckades', description, variant: 'error' });
    };

    const handleAppStorePurchaseError = (event: Event) => {
      const detail = (event as CustomEvent).detail as { cancelled: boolean; code?: string; message?: string; [key: string]: unknown };
      const { cancelled, code, message } = detail;
      if (cancelled) return;

      const isAlreadyOwned = code?.toUpperCase().includes('ALREADY_OWNED')
        || /already owned/i.test(message || '');
      if (isAlreadyOwned && nativePlatform === 'ios') {
        appWindow.ReactNativeWebView?.postMessage(JSON.stringify({
          type: 'CHECK_APPLE_PREMIUM',
          productId: IOS_PREMIUM_PRODUCT_ID,
          initiatedByPurchase: true,
        }));
        return;
      }

      const baseMessage = message?.trim() || 'Tekniskt fel vid köp i App Store.';
      const codeLabel = code || 'okänd';
      const queriedProductIds = Array.isArray(detail.queriedProductIds)
        ? detail.queriedProductIds.filter((value): value is string => typeof value === 'string' && value.length > 0)
        : [];
      const runtimeStoreIdentifier = typeof detail.runtimeStoreIdentifier === 'string'
        ? detail.runtimeStoreIdentifier
        : '';
      const requestedProductId = typeof detail.requestedProductId === 'string'
        ? detail.requestedProductId
        : '';
      const debugParts: string[] = [];

      if (requestedProductId) {
        debugParts.push(`begärd produkt: ${requestedProductId}`);
      }

      if (runtimeStoreIdentifier) {
        debugParts.push(`bundle ID: ${runtimeStoreIdentifier}`);
      }

      if (queriedProductIds.length > 0) {
        debugParts.push(`returnerade produkter: ${queriedProductIds.join(', ')}`);
      }

      const debugDetails = debugParts.length > 0
        ? ` Detaljer: ${debugParts.join(' | ')}.`
        : (!code && !message ? ` Detaljer: ${JSON.stringify(detail)}` : '');
      const description = `${baseMessage} (kod: ${codeLabel})${debugDetails}`;

      pushToast({ title: 'Köpet misslyckades', description, variant: 'error' });
    };

    window.addEventListener('googlePlayPurchase', handleGooglePlayPurchase);
    window.addEventListener('appStorePurchase', handleAppStorePurchase);
    window.addEventListener('nativeApplePremiumCheckResult', handleApplePremiumCheckResult);
    window.addEventListener('googlePlayPurchaseError', handleGooglePlayPurchaseError);
    window.addEventListener('appStorePurchaseError', handleAppStorePurchaseError);

    if (!user?.id) {
      appleOwnershipCheckUserIdRef.current = null;
    } else if (
      nativePlatform === 'ios'
      && user.subscriptionTier !== 'premium'
      && appleOwnershipCheckUserIdRef.current !== user.id
      && appWindow.ReactNativeWebView
    ) {
      appleOwnershipCheckUserIdRef.current = user.id;
      appWindow.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'CHECK_APPLE_PREMIUM',
        productId: IOS_PREMIUM_PRODUCT_ID,
      }));
    }

    return () => {
      window.removeEventListener('googlePlayPurchase', handleGooglePlayPurchase);
      window.removeEventListener('appStorePurchase', handleAppStorePurchase);
      window.removeEventListener('nativeApplePremiumCheckResult', handleApplePremiumCheckResult);
      window.removeEventListener('googlePlayPurchaseError', handleGooglePlayPurchaseError);
      window.removeEventListener('appStorePurchaseError', handleAppStorePurchaseError);
    };
  }, [appWindow.ReactNativeWebView, fetchUser, nativePlatform, pushToast, t, user?.id, user?.subscriptionTier]);

  // Stable ref so Paddle.Initialize's eventCallback can reach latest React callbacks
  const paddleCallbackRef = useRef<{ fetchUser: () => Promise<void>; pushToast: typeof pushToast; pollForPremium: () => Promise<void> } | null>(null);

  // Poll /users/me until subscriptionTier === 'premium', then show welcome modal.
  // Retries every 2s for up to 20s to handle webhook processing delay.
  const pollForPremium = useCallback(async () => {
    const { apiGet } = await import('@/services/api-client');
    const maxAttempts = 5;
    const intervalMs = 2000;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const result = await apiGet<{ subscriptionTier?: string }>('/users/me');
      if (result.success && result.data?.subscriptionTier === 'premium') {
        await paddleCallbackRef.current?.fetchUser();
        setPremiumActivationStatus('success');
        return;
      }
      if (attempt < maxAttempts - 1) {
        await new Promise((r) => setTimeout(r, intervalMs));
      }
    }
    // Timed out — webhook may have failed
    setPremiumActivationStatus('failed');
  }, []);

  // Update ref after pollForPremium is defined
  paddleCallbackRef.current = { fetchUser, pushToast, pollForPremium };

  // Paddle-checkout för webbköp
  const openPaddleCheckout = useCallback(async () => {
    const clientToken = import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string | undefined;
    const priceId = import.meta.env.VITE_PADDLE_PRICE_ID as string | undefined;

    if (!clientToken || !priceId) {
      setShowPremiumInfoModal(true);
      return;
    }

    try {
      if (!appWindow._paddleInitialized) {
        if (!appWindow.Paddle) {
          await new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Paddle.js'));
            document.head.appendChild(script);
          });
        }
        const Paddle = appWindow.Paddle;
        if (!Paddle) {
          throw new Error('Paddle SDK is not available');
        }
        const env = (import.meta.env.VITE_PADDLE_ENVIRONMENT as string) || 'sandbox';
        Paddle.Environment.set(env);
        Paddle.Initialize({
          token: clientToken,
          eventCallback: (event) => {
            if (event.name === 'checkout.completed') {
              // Show activation modal immediately while polling
              setPremiumActivationStatus('polling');
              // Close Paddle's success screen after it has rendered
              setTimeout(() => appWindow.Paddle?.Checkout?.close?.(), 1500);
              // Poll backend until webhook has been processed (subscription.activated)
              setTimeout(() => paddleCallbackRef.current?.pollForPremium(), 500);
            }
          },
        });
        appWindow._paddleInitialized = true;
      }

      const Paddle = appWindow.Paddle;
      if (!Paddle) {
        throw new Error('Paddle SDK is not available');
      }
      Paddle.Checkout.open({
        items: [{ priceId, quantity: 1 }],
        customer: { email: user?.email },
        customData: JSON.stringify({ userId: user?.id }),
        settings: { displayMode: 'overlay' },
      });
    } catch (err) {
      pushToast({ title: 'Något gick fel', description: 'Kunde inte öppna betalningssidan.', variant: 'error' });
    }
  }, [appWindow, pushToast, user?.email, user?.id]);

  const openPaddlePortal = useCallback(async () => {
    const { apiGet } = await import('@/services/api-client');
    const res = await apiGet<{ url: string }>('/premium/paddle-portal');
    if (res.success && res.data?.url) {
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } else {
      pushToast({ title: 'Fel', description: 'Kunde inte öppna betalningsportalen. Försök igen.', variant: 'error' });
    }
  }, [pushToast]);

  // Autosave feedback
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Autosave helper
  const triggerAutosave = () => {
    setSaveStatus('saving');
    // Simulera API-anrop (här skulle man spara till backend)
    setTimeout(() => {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }, 500);
  };

  const handlePostalCodeChange = (value: string) => {
    setPostalCode(value);
    setPostalCodeError('');
    
    // Rensa postnummer och kolla längd
    const cleaned = value.replace(/[\s-]/g, '');
    if (cleaned.length >= 2) {
      const region = getRegionFromPostalCode(cleaned);
      if (region) {
        setVmaRegion(region, value);
        setPostalCodeError('');
        triggerAutosave();
      } else if (cleaned.length === 5) {
        setPostalCodeError('Kunde inte hitta region för detta postnummer');
      }
    }
  };

  const handleNotificationChange = (key: keyof typeof notifications, value: boolean) => {
    setNotification(key, value);
    triggerAutosave();
  };

  const handleEnableNotifications = async () => {
    setIsRequestingPermission(true);
    try {
      const granted = await requestPermission();
      if (granted) {
        pushToast({
          title: 'Notifikationer aktiverade',
          description: 'Du kommer nu att få notifikationer i webbläsaren',
          variant: 'success'
        });
      } else {
        pushToast({
          title: 'Notifikationer nekade',
          description: 'Du kan aktivera notifikationer i webbläsarens inställningar',
          variant: 'error'
        });
      }
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      pushToast({
        title: 'Fel',
        description: 'Kunde inte aktivera notifikationer',
        variant: 'error'
      });
    } finally {
      setIsRequestingPermission(false);
    }
  };

  const handleRegionChange = (region: string) => {
    setVmaRegion(region);
    setPostalCode('');
    setPostalCodeError('');
    if (region) triggerAutosave();
  };

  const handleLanguageChange = (newLang: string) => {
    setLanguage(newLang);
    i18n.changeLanguage(newLang);
    triggerAutosave();
  };
  
  const handleHouseholdDisplayNameChange = (value: string) => {
    // Begränsa till max 20 tecken
    const trimmedValue = value.slice(0, 20);
    setHouseholdDisplayName(trimmedValue);
    const current = user?.settings?.householdDisplayName || '';
    setHouseholdDisplayNameChanged(trimmedValue !== current);
  };

  const handleSaveHouseholdDisplayName = async () => {
    if (isMemberReadOnly) return;
    if (!user) return;
    const current = user.settings?.householdDisplayName || '';
    if ((householdDisplayName || '') === current) return;
    setSaveStatus('saving');
    const result = await updateUser({ settings: { ...(user.settings || {}), householdDisplayName: householdDisplayName || undefined } });
    if (result.success) {
      setHouseholdDisplayNameChanged(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
      pushToast({
        title: 'Sparat',
        description: 'Hushållets visningsnamn har uppdaterats',
        variant: 'success'
      });
    } else {
      setSaveStatus('idle');
      pushToast({
        title: 'Fel',
        description: result.error || 'Kunde inte spara visningsnamn',
        variant: 'error'
      });
    }
  };

  const handleSaveDisplayName = async () => {
    if (!user) return;
    if (!displayName || displayName === user.displayName) return;

    const normalizedDisplayName = displayName.trim().toLocaleLowerCase('sv-SE');
    if (FORBIDDEN_DISPLAY_NAMES.includes(normalizedDisplayName)) {
      pushToast({
        title: 'Ogiltigt visningsnamn',
        description: 'Detta visningsnamn är inte tillåtet',
        variant: 'error'
      });
      return;
    }
    
    // Frontend-validering: minst 5 tecken
    if (displayName.trim().length < 5) {
      pushToast({
        title: 'Ogiltigt visningsnamn',
        description: 'Visningsnamnet måste vara minst 5 tecken långt',
        variant: 'error'
      });
      return;
    }
    
    setSaveStatus('saving');
    const result = await updateUser({ displayName });
    if (result.success) {
      setDisplayNameChanged(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
      pushToast({
        title: 'Sparat',
        description: 'Ditt visningsnamn har uppdaterats',
        variant: 'success'
      });
    } else {
      setSaveStatus('idle');
      pushToast({
        title: 'Kunde inte spara visningsnamn',
        description: result.error || 'Ett oväntat fel uppstod',
        variant: 'error'
      });
    }
  };

  const handleAddFamilyMember = () => {
    if (!newMemberName.trim()) return;
    addFamilyMember({ 
      name: newMemberName, 
      role: newMemberRole 
    });
    setNewMemberName('');
    setNewMemberRole('adult');
    setNewMemberCalories(2000);
    setShowAddMember(false);
  };

  const handleUpdateCalories = (id: string, change: number) => {
    const member = familyMembers.find(m => m.id === id);
    if (!member) return;
    
    const currentCalories = member.dailyCalories || (member.role === 'adult' ? 2000 : 1500);
    const newCalories = Math.max(0, Math.min(5000, currentCalories + change));
    
    updateFamilyMember(id, { dailyCalories: newCalories });
  };

  const handleRemoveFamilyMember = (id: string) => {
    removeFamilyMember(id);
  };

  const handleDeleteAccount = async () => {
    if (user?.subscriptionTier === 'premium') {
      pushToast({
        title: t('settings.account.deleteBlockedTitle', 'Avsluta Premium först'),
        description: t('settings.account.deleteBlockedDesc', 'Du kan inte radera kontot så länge Premium är aktivt. Avsluta din prenumeration först. Kontakta support.'),
        variant: 'error'
      });
      return;
    }

    // Call backend to delete account using authenticated API client
    try {
      const res = await import('@/services/api-client').then(m => m.apiDelete('/users/me'));
      if (res && res.success) {
        pushToast({ title: t('settings.account.deleteSuccess', 'Konto raderat'), description: t('settings.account.deleteSuccessDesc', 'Ditt konto och data har raderats.'), variant: 'success' });
        // Ensure local app state and storage are fully cleared, then hard-navigate to start
        try { logoutLocal?.(); } catch { /* ignore */ }
        try { window.location.replace('/'); } catch { /* ignore */ }
      } else {
        pushToast({ title: t('settings.account.deleteFailed', 'Kunde inte radera konto'), description: res?.error || t('settings.account.deleteFailedDesc', 'Ett fel uppstod.'), variant: 'error' });
      }
    } catch (err) {
      pushToast({ title: t('settings.account.deleteFailed', 'Kunde inte radera konto'), description: String(err), variant: 'error' });
    }
  };

  // Export/Import handlers
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const profileData = await exportProfile();
      downloadProfileExport(profileData, user?.displayName || 'profil');
      pushToast({
        title: 'Export lyckades',
        description: 'Din profil har exporterats till en JSON-fil',
        variant: 'success'
      });
    } catch (error) {
      console.error('Export error:', error);
      pushToast({
        title: 'Export misslyckades',
        description: error instanceof Error ? error.message : 'Ett oväntat fel uppstod',
        variant: 'error'
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleImport = async (file: File) => {
    // Check if Premium
    const isPremium = user?.subscriptionTier === 'premium';
    if (!isPremium) {
      setPremiumFeature('Importera beredskapsdata');
      setShowPremiumModal(true);
      return;
    }

    // Check if family member (not owner)
    if (family && !isFamilyOwner) {
      pushToast({
        title: 'Åtkomst nekad',
        description: 'Endast familjeägare kan importera beredskapsdata',
        variant: 'error'
      });
      return;
    }

    setIsImporting(true);
    try {
      const profileData = await readProfileExport(file);
      await importProfile(profileData);
      pushToast({
        title: 'Import lyckades',
        description: 'Din profil har importerats. Uppdatera sidan för att se ändringarna.',
        variant: 'success'
      });
      // Reload page after a short delay to show all imported data
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error('Import error:', error);
      pushToast({
        title: 'Import misslyckades',
        description: error instanceof Error ? error.message : 'Ett oväntat fel uppstod',
        variant: 'error'
      });
    } finally {
      setIsImporting(false);
    }
  };

  const prepGoalOptions = [
    { value: '7d', label: t('prepLevels.7d'), description: t('prepLevels.7dDesc'), tier: 'free' as const },
    { value: '30d', label: t('prepLevels.30d'), description: t('prepLevels.30dDesc'), tier: 'premium' as const },
    { value: '90d', label: t('prepLevels.90d'), description: t('prepLevels.90dDesc'), tier: 'premium' as const },
  ];

  const roleLabels: Record<string, string> = {
    adult: t('settings.family.roles.adult'),
    child: t('settings.family.roles.child'),
    pet: t('settings.family.roles.pet'),
  };

  return (
    <div className="space-y-8 max-w-5xl overflow-x-hidden">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center"><Settings className="h-6 w-6 text-primary mr-3 shrink-0" />{t('settings.title')}</h1>
          <p className="mt-2 text-muted-foreground">
            {t('settings.subtitle')}
          </p>
        </div>
        {/* Autosave indicator */}
        {saveStatus !== 'idle' && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-opacity ${
            saveStatus === 'saving' ? 'bg-muted text-muted-foreground' : 'bg-primary/20 text-primary'
          }`}>
            {saveStatus === 'saving' ? (
              <>
                <span className="w-3 h-3 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                {t('settings.saving')}
              </>
            ) : (
              <>
                <Check className="h-3 w-3" />
                {t('settings.saved')}
              </>
            )}
          </div>
        )}
      </div>

      {/* Profile */}
      <section className="rounded-xl border border-border bg-card p-6 overflow-hidden">
        <div className="flex items-center gap-3 mb-6">
          <User className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">{t('settings.profile.title')}</h2>
        </div>
        <div className="space-y-4 min-w-0">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('settings.profile.email')}
            </label>
            <div className="flex items-center gap-2 min-w-0">
              <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-muted-foreground soft-wrap min-w-0">{email || t('settings.profile.noEmail')}</span>
            </div>
          </div>
          <div className="min-w-0">
            <label className="block text-sm font-medium mb-1">
              {t('settings.profile.displayName')}
              <span className="ml-2 text-xs text-muted-foreground">{displayName.length}/50</span>
            </label>
            <p className="text-sm text-muted-foreground mb-2">
              Visningsnamnet kan ses av dig, din familj och medlemmar i grupper som du är medlem i.
            </p>
            <div className="grid gap-2" style={{ gridTemplateColumns: '1fr auto' }}>
              <input
                type="text"
                size={1}
                maxLength={50}
                value={displayName}
                onChange={(e) => {
                  setDisplayName(e.target.value);
                  setDisplayNameChanged(e.target.value !== user?.displayName);
                }}
                className="rounded-lg border border-border bg-background px-3 py-2 min-w-0"
              />
              {displayNameChanged && (
                <button
                  onClick={handleSaveDisplayName}
                  disabled={saveStatus === 'saving' || displayName.trim().length < 5}
                  className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                  style={{ backgroundColor: '#67724e' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#58613f'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#67724e'}
                >
                  {saveStatus === 'saving' ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline">Spara</span>
                </button>
              )}
            </div>
            {displayName.trim().length > 0 && displayName.trim().length < 5 && (
              <p className="text-sm text-red-500 mt-1">Visningsnamnet måste vara minst 5 tecken långt</p>
            )}
            {user?.displayNameLastChangedAt && (() => {
              const lastChanged = new Date(user.displayNameLastChangedAt);
              const now = new Date();
              const daysSinceChange = (now.getTime() - lastChanged.getTime()) / (1000 * 60 * 60 * 24);
              const daysUntilCanChange = Math.max(0, Math.ceil(7 - daysSinceChange));
              
              if (daysUntilCanChange > 0) {
                return (
                  <p className="text-sm text-muted-foreground mt-1">
                    Du kan byta visningsnamn igen om {daysUntilCanChange} {daysUntilCanChange === 1 ? 'dag' : 'dagar'}
                  </p>
                );
              } else {
                return (
                  <p className="text-sm text-muted-foreground mt-1">
                    Senast ändrat: {lastChanged.toLocaleDateString('sv-SE')}
                  </p>
                );
              }
            })()}
          </div>
        </div>
      </section>

      {/* Premium */}
      <span id="get-premium" className="block scroll-mt-20" />
      <section id="premium" className="rounded-xl border border-border bg-card p-6 scroll-mt-20">
        <div className="flex items-center gap-3 mb-6">
          <CreditCard className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">{t('settings.premium.title', 'Premium')}</h2>
        </div>
        <div className="mb-2">
        </div>
        {/* Comparison table */}
        <div className="mt-6">
          <h3 className="text-sm font-semibold mb-3">{t('settings.premium.compare.title', 'Planer:')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(() => {
              const isPremium = user?.subscriptionTier === 'premium';
              return (
                <>
                  <div className={`rounded-lg p-4 border ${!isPremium ? 'border-orange-500 bg-primary/10' : 'border-border bg-card'}`}>
                    <div className="font-medium text-base sm:text-lg mb-2 flex items-center justify-between gap-2">
                      <div>
                        <span>{t('settings.premium.compare.freeTitle','Gratis')}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
                          if (isDesktop) {
                            const next = !showFreePlanDetails || !showPremiumPlanDetails;
                            setShowFreePlanDetails(next);
                            setShowPremiumPlanDetails(next);
                          } else {
                            setShowFreePlanDetails((v) => !v);
                          }
                        }}
                        className="text-xs text-primary hover:underline"
                      >
                        {showFreePlanDetails ? 'Dölj detaljer' : 'Visa detaljer'}
                      </button>
                    </div>
                    {showFreePlanDetails && (
                      <ul className="text-sm text-muted-foreground space-y-1.5 mt-2 list-disc ml-4">
                        <li>Beredskapsplanering 7 dagar</li>
                        <li>Checklistor för viktiga förberedelser</li>
                        <li>Beredskapsförråd (inventering och rotering)</li>
                        <li>Vattenförråd</li>
                        <li>Inköpslistor för mat och utrustning</li>
                        <li>Kunskapsdatabas</li>
                        <li>Familjeläge: delta som familjemedlem</li>
                        <li>Gå med i upp till 2 beredskapsgrupper</li>
                      </ul>
                    )}
                  </div>
                  <div className={`rounded-lg p-4 border ${isPremium ? 'border-orange-500 bg-primary/10' : 'border-border bg-card'}`}>
                    <div className="font-medium text-base sm:text-lg mb-2 flex items-center justify-between gap-2">
                      <Crown className="h-4 w-4 text-amber-500 dark:text-amber-400" />
                      <div className="flex-1 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <span>{t('settings.premium.compare.premiumTitle','Premium')}</span>
                          <span className="text-sm font-normal text-muted-foreground">{t('settings.premium.price', '69 kr/mån')}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
                            if (isDesktop) {
                              const next = !showPremiumPlanDetails || !showFreePlanDetails;
                              setShowFreePlanDetails(next);
                              setShowPremiumPlanDetails(next);
                            } else {
                              setShowPremiumPlanDetails((v) => !v);
                            }
                          }}
                          className="text-xs text-primary hover:underline whitespace-nowrap"
                        >
                          {showPremiumPlanDetails ? 'Dölj detaljer' : 'Visa detaljer'}
                        </button>
                      </div>
                    </div>
                    {showPremiumPlanDetails && (
                      <ul className="text-sm text-muted-foreground space-y-1.5 mt-2 list-disc ml-4">
                        <li>Allt i Gratis-planen</li>
                        <li>Beredskapsplanering 7, 30 och 90 dagar+</li>
                        <li>Årshjul för återkommande beredskapsplanering</li>
                        <li>Receptbok för matplanering i vardag och kris</li>
                        <li>Anteckningsbok för beredskapsplanering</li>
                        <li>Familjeläge: skapa och bjuda in familjemedlemmar</li>
                        <li>Upp till 50 beredskapsgrupper</li>
                        <li>Skapa egna grupper och bjuda in medlemmar</li>
                        <li>Full tillgång till gruppdiskussioner och aktiviteter</li>
                        <li>Personlig support via chatt</li>
                      </ul>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
        {/* Action button under table */}
        <div className="mt-4 flex flex-col gap-2">
          {user?.subscriptionTier === 'premium' ? (
            <>
              {showGooglePlayManagement ? (
                <button
                  onClick={() => setPremiumManagementInfoStore('google')}
                  className="px-4 py-2 rounded-lg border hover:bg-muted"
                >
                  {t('settings.premium.manageInGooglePlay', 'Hantera i Google Play')}
                </button>
              ) : showAppStoreManagement ? (
                <button
                  onClick={() => setPremiumManagementInfoStore('apple')}
                  className="px-4 py-2 rounded-lg border hover:bg-muted"
                >
                  {t('settings.premium.manageInAppStore', 'Hantera i App Store')}
                </button>
              ) : showWebSubscriptionManagement && !canOpenPaddlePortal ? (
                <button
                  onClick={() => setPremiumManagementInfoStore('web')}
                  className="px-4 py-2 rounded-lg border hover:bg-muted"
                >
                  {t('settings.premium.manageOnWeb', 'Hantera på webben')}
                </button>
              ) : canDirectlyDisablePremium ? (
                <button
                  onClick={() => setConfirmDisablePremiumOpen(true)}
                  className="px-4 py-2 rounded-lg border hover:bg-muted"
                >
                  {t('settings.premium.disable','Avsluta Premium')}
                </button>
              ) : null}
              {canOpenPaddlePortal && (
                <>
                  {user?.subscriptionActivatedAt && (
                    <p className="text-sm text-muted-foreground">{t('settings.premium.activatedAt', { date: new Date(user.subscriptionActivatedAt).toLocaleString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' }) })}</p>
                  )}
                  <button
                    onClick={openPaddlePortal}
                    className="px-4 py-2 rounded-lg border hover:bg-muted text-sm"
                  >
                    {t('settings.premium.manageSubscription', 'Hantera prenumeration')}
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  if (!startNativePremiumPurchase()) {
                    openPaddleCheckout();
                  }
                }}
                className="w-full px-6 py-3 rounded-lg bg-amber-600/30 hover:bg-amber-600/40 text-amber-100 font-semibold transition-all border-2 border-amber-500 shadow-[0_0_20px_rgba(245,158,11,0.5)] hover:shadow-[0_0_30px_rgba(245,158,11,0.7)] flex items-center justify-center gap-2"
              >
                <Crown className="h-5 w-5" />
                {t('settings.premium.enable', 'Skaffa Premium')}
              </button>
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">{t('settings.premium.subscription.title', 'Premium')}</p>
                <p>{t('settings.premium.subscription.duration', 'Längd: 1 månad.')}</p>
                <p>{t('settings.premium.subscription.price', 'Pris: 69 kr per månad.')}</p>
                <p>{t('settings.premium.subscription.renewal', 'Prenumerationen förnyas automatiskt tills du avslutar den.')}</p>
                <p>
                  <Link to="/terms" className="underline hover:text-foreground transition-colors">
                    {t('settings.premium.subscription.terms', 'Användarvillkor (EULA)')}
                  </Link>
                  {' '}•{' '}
                  <Link to="/privacy" className="underline hover:text-foreground transition-colors">
                    {t('settings.premium.subscription.privacy', 'Integritetspolicy')}
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Import/Export preparedness data */}
      <section id="export-import" className="rounded-xl border border-border bg-card p-6 scroll-mt-20">
        <div className="flex items-center gap-3 mb-6">
          <Download className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Importera/Exportera beredskapsdata</h2>
        </div>

        <p className="text-sm text-muted-foreground mb-6">
          Exportera din beredskapsdata till en JSON-fil som kan importeras av dig själv eller andra Premium-användare.
          Inkluderar: förråd, vatteninställningar och behållare, inköpslistor, inköpslista-artiklar och favoriter,
          checklistor (standard och egna), årshjul, anteckningar, recept, matsedel och kunskapslässtatus.
          Exkluderar: familjeinställningar, användarinställningar och kontouppgifter.
        </p>

        <div className="mb-6">
          <button
            type="button"
            onClick={() => setShowExportInfo((v) => !v)}
            className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-border bg-background text-[11px] font-semibold">
              i
            </span>
            <span>{showExportInfo ? 'Dölj detaljer om exporten' : 'Visa exakt vad som ingår i exporten'}</span>
          </button>
          {showExportInfo && (
            <div className="mt-3 text-xs text-muted-foreground space-y-1">
              <p className="font-medium text-foreground">Detta ingår i en export:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li><span className="font-medium">Förråd:</span> alla föremål i förrådet.</li>
                <li><span className="font-medium">Vatten:</span> hushållsstorlek, husdjur, måldagar och alla vattenbehållare.</li>
                <li><span className="font-medium">Inköpslista:</span> alla inköpslistor, tillhörande list-objekt samt favoriter.</li>
                <li><span className="font-medium">Checklista:</span> progress på standardlistor samt egna checklistor och deras uppgifter.</li>
                <li><span className="font-medium">Årshjul:</span> alla årshjulsuppgifter.</li>
                <li><span className="font-medium">Anteckningar:</span> alla anteckningar.</li>
                <li><span className="font-medium">Receptbok:</span> alla recept samt din aktuella matsedel.</li>
                <li><span className="font-medium">Kunskap:</span> lässtatus för artiklar i kunskapsbasen.</li>
              </ul>
              <p className="mt-2">
                Exporten är profiloberoende: användar-id, familje-id och inloggningsuppgifter följer inte med,
                utan datan kopplas till den användare som importerar filen.
              </p>
            </div>
          )}
        </div>

        {/* Premium lock warning if not premium */}
        {user?.subscriptionTier !== 'premium' && (
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <Crown className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-600 dark:text-yellow-400">Premium-funktion</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Import av beredskapsdata är endast tillgängligt för Premium-användare. Export av beredskapsdata
                  är tillgängligt för alla användare.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Family member warning if not owner */}
        {family && !isFamilyOwner && (
          <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <Lock className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-orange-600 dark:text-orange-400">Endast familjeägare</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Familjemedlemmar kan exportera beredskapsdata till JSON, men endast familjeägaren kan importera beredskapsdata.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          {/* Export Button */}
          <button
            onClick={() => setShowExportConfirm(true)}
            disabled={isExporting}
            className="flex items-center justify-center gap-3 px-6 py-4 rounded-lg border-2 border-border bg-card hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <RefreshCw className="h-5 w-5 animate-spin" />
                <span className="font-medium">Exporterar...</span>
              </>
            ) : (
              <>
                <Download className="h-5 w-5" />
                <span className="font-medium">Exportera beredskapsdata</span>
              </>
            )}
          </button>

          {/* Import Button */}
          <label className={`flex items-center justify-center gap-3 px-6 py-4 rounded-lg border-2 border-border bg-card hover:bg-muted transition-colors ${
            isImporting || (family && !isFamilyOwner) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}>
            {isImporting ? (
              <>
                <RefreshCw className="h-5 w-5 animate-spin" />
                <span className="font-medium">Importerar...</span>
              </>
            ) : (
              <>
                <Upload className="h-5 w-5" />
                <span className="font-medium">Importera beredskapsdata</span>
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setShowImportConfirm(true);
                      // Temporärt spara filen på window för att kunna nå den från modalen
                      // (enklaste lösningen utan att ändra större struktur). Den rensas efter användning.
                      appWindow.__pendingImportFile = file;
                      // Reset input so same file can be selected again
                      e.target.value = '';
                    }
                  }}
                  disabled={isImporting || (family ? !isFamilyOwner : false)}
                  className="hidden"
                />
              </>
            )}
          </label>
        </div>

        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Tips:</strong> Exporterad data är profiloberoende och kan importeras 
            av andra Premium-användare.
          </p>
        </div>
      </section>

      {/* Family Members */}
      <section id="family" className="rounded-xl border border-border bg-card p-6 scroll-mt-20">
        <div className="flex items-center gap-3 mb-6">
          <Users className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">{t('settings.family.title')}</h2>
        </div>

        {!isFamilyOwner && (
          <p className="text-sm text-muted-foreground mb-4">{t('settings.family.viewOnlyOwner')}</p>
        )}
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">
            Visningsnamn för hushåll (valfritt)
          </label>
          <div className="grid gap-2" style={{ gridTemplateColumns: '1fr auto' }}>
            <input
              type="text"
              size={1}
              value={householdDisplayName}
              onChange={(e) => handleHouseholdDisplayNameChange(e.target.value)}
              placeholder="T.ex. Familjen Andersson"
              maxLength={20}
              readOnly={isMemberReadOnly}
              className={`px-4 py-2 rounded-lg border border-border min-w-0 ${isMemberReadOnly ? 'bg-muted/30 text-muted-foreground cursor-not-allowed' : 'bg-background focus:ring-2 focus:ring-primary focus:border-primary'}`}
            />
            {householdDisplayNameChanged && !isMemberReadOnly && (
              <button
                onClick={handleSaveHouseholdDisplayName}
                disabled={saveStatus === 'saving'}
                className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                style={{ backgroundColor: '#67724e' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#58613f'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#67724e'}
              >
                {saveStatus === 'saving' ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Spara</span>
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Detta namn används på Översikts-sidan istället för "Din beredskap"
          </p>
        </div>
        
        <p className="text-sm text-muted-foreground mb-4">
          {t('settings.family.description')}
        </p>

        <div className="space-y-3">
          {/* Dig själv */}
          <div className="p-3 rounded-lg bg-muted/50 space-y-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium soft-wrap">{displayName || t('common.you')}</div>
                <div className="text-sm text-muted-foreground">{t('settings.family.roles.adult')} ({t('settings.family.accountHolder')})</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 pl-[52px]">
              <button
                onClick={() => setUserDailyCalories(Math.max(0, userDailyCalories - 100))}
                className={`w-7 h-7 rounded bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors flex-shrink-0 ${isMemberReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                disabled={isMemberReadOnly || userDailyCalories === 0}
              >
                <span className="text-base font-medium">−</span>
              </button>
              <div className="text-sm font-medium text-center flex-1 min-w-0">
                {userDailyCalories} kcal/dag
              </div>
              <button
                onClick={() => setUserDailyCalories(Math.min(5000, userDailyCalories + 100))}
                className={`w-7 h-7 rounded bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors flex-shrink-0 ${isMemberReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                disabled={isMemberReadOnly || userDailyCalories === 5000}
              >
                <span className="text-base font-medium">+</span>
              </button>
            </div>
            {isMemberReadOnly && (
              <p className="text-xs text-muted-foreground mt-2">{t('settings.family.viewOnlyOwner')}</p>
            )}
          </div>

          {/* Family members */}
          {familyMembers.map((member) => {
            const calories = member.dailyCalories || (member.role === 'adult' ? 2000 : member.role === 'child' ? 1500 : undefined);
            
            return (
              <div key={member.id} className="p-3 rounded-lg bg-muted/50 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <User className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="font-medium">{member.name}</div>
                      <div className="text-sm text-muted-foreground">{roleLabels[member.role]}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => { if (!isMemberReadOnly) handleRemoveFamilyMember(member.id); }}
                    disabled={isMemberReadOnly}
                    className={`p-2 rounded transition-colors ${isMemberReadOnly ? 'opacity-60 cursor-not-allowed' : 'hover:bg-red-500/20 hover:text-red-400'}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                
                {member.role !== 'pet' && (
                  <div className="flex items-center gap-2 pl-[52px]">
                    <button
                      onClick={() => handleUpdateCalories(member.id, -100)}
                      className="w-7 h-7 rounded bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors flex-shrink-0"
                      disabled={isMemberReadOnly || calories === 0}
                    >
                      <span className="text-base font-medium">−</span>
                    </button>
                    <div className="text-sm font-medium text-center flex-1 min-w-0">
                      {calories} kcal/dag
                    </div>
                    <button
                      onClick={() => handleUpdateCalories(member.id, 100)}
                      className="w-7 h-7 rounded bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors flex-shrink-0"
                      disabled={isMemberReadOnly || calories === 5000}
                    >
                      <span className="text-base font-medium">+</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Add member button */}
        {!isMemberReadOnly && (
          <button
            onClick={() => setShowAddMember(true)}
            className="mt-4 w-full px-4 py-2 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
            style={{ backgroundColor: '#67724e' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#58613f'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#67724e'}
          >
            <Plus className="h-4 w-4" />
            {t('settings.family.addMember')}
          </button>
        )}

        {/* Add member form */}
        {showAddMember && !isMemberReadOnly && (
          <div className="mt-4 p-4 rounded-lg border border-border bg-background">
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">{t('settings.family.name')}</label>
                  <span className="text-xs text-muted-foreground">{newMemberName.length}/50</span>
                </div>
                <input
                  type="text"
                  maxLength={50}
                  value={newMemberName}
                  onChange={(e) => setNewMemberName(e.target.value)}
                  placeholder={t('settings.family.name')}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2"
                />
              </div>
              <select
                value={newMemberRole}
                onChange={(e) => {
                  const role = e.target.value as typeof newMemberRole;
                  setNewMemberRole(role);
                  // Uppdatera standardvärde baserat på roll
                  if (role === 'adult') setNewMemberCalories(2000);
                  else if (role === 'child') setNewMemberCalories(1500);
                }}
                className="w-full rounded-lg border border-border bg-background px-3 py-2"
              >
                <option value="adult">{t('settings.family.roles.adult')}</option>
                <option value="child">{t('settings.family.roles.child')}</option>
                <option value="pet">{t('settings.family.roles.pet')}</option>
              </select>
              
              {newMemberRole !== 'pet' && (
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">
                    {t('settings.family.dailyCalories')}
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setNewMemberCalories(Math.max(0, newMemberCalories - 100))}
                      className="w-10 h-10 rounded bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
                      disabled={newMemberCalories === 0}
                    >
                      <span className="text-lg font-medium">−</span>
                    </button>
                    <input
                      type="number"
                      value={newMemberCalories}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setNewMemberCalories(Math.max(0, Math.min(5000, val)));
                      }}
                      min="0"
                      max="5000"
                      step="100"
                      className="flex-1 text-center rounded-lg border border-border bg-background px-3 py-2"
                    />
                    <span className="text-sm text-muted-foreground min-w-[50px]">kcal</span>
                    <button
                      type="button"
                      onClick={() => setNewMemberCalories(Math.min(5000, newMemberCalories + 100))}
                      className="w-10 h-10 rounded bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
                      disabled={newMemberCalories === 5000}
                    >
                      <span className="text-lg font-medium">+</span>
                    </button>
                  </div>
                </div>
              )}
              
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddMember(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleAddFamilyMember}
                  className="flex-1 px-4 py-2 rounded-lg text-white transition-colors"
                  style={{ backgroundColor: '#67724e' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#58613f'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#67724e'}
                >
                  {t('common.add')}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Prep Goal */}
      <section id="prep-goal" className="rounded-xl border border-border bg-card p-6 scroll-mt-4">
        <div className="flex items-center gap-3 mb-6">
          <Target className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">{t('settings.prepGoal.title')}</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          {t('settings.prepGoal.description')}
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          {prepGoalOptions.map((option) => {
            const isPremiumLocked = option.tier === 'premium' && user?.subscriptionTier !== 'premium';
            const isMemberReadOnly = !!family && !isFamilyOwner;
            return (
              <button
                key={option.value}
                onClick={() => {
                  if (isMemberReadOnly) return; // read-only for family members
                  if (isPremiumLocked) {
                    // Show Premium modal instead of scrolling
                    setPremiumFeature(`Beredskapsmål för ${option.label}`);
                    setShowPremiumModal(true);
                    return;
                  }
                  // Open confirmation modal before applying change
                  setPendingGoal(option.value as "7d" | "30d" | "90d");
                  setConfirmOpen(true);
                }}
                aria-disabled={isMemberReadOnly && prepGoal !== option.value}
                className={`text-left p-4 rounded-lg border transition-all relative flex flex-col items-start ${
                  prepGoal === option.value
                    ? 'border-primary bg-primary/10 ring-2 ring-primary ring-offset-2 ring-offset-background'
                    : isPremiumLocked
                    ? 'border-border bg-card hover:border-amber-500/50 hover:bg-amber-500/5 cursor-pointer'
                    : isMemberReadOnly
                    ? 'border-border bg-muted/20 opacity-80 cursor-not-allowed'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center justify-between gap-2 w-full">
                  <span className="font-semibold">{option.label}</span>
                  {option.tier === 'free' ? (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-600">
                      {t('prepLevels.free')}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-600">
                      {t('prepLevels.premium')}
                    </span>
                  )}
                </div>
                <div className="text-sm text-muted-foreground mt-1">{option.description}</div>
                {isPremiumLocked && (
                  <div className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    {t('prepLevels.upgradeToUnlock')}
                  </div>
                )}
              </button>
            );
          })}
          
          <ConfirmationModal
            open={confirmOpen}
            title={t('settings.prepGoal.confirmTitle', 'Bekräfta ändring')}
            description={t('settings.prepGoal.confirmDesc', 'Vill du verkligen ändra beredskapsmål?')}
            confirmLabel={t('common.confirm', 'Bekräfta')}
            cancelLabel={t('common.cancel', 'Avbryt')}
            onConfirm={() => {
              if (pendingGoal) setPrepGoal(pendingGoal);
              setConfirmOpen(false);
              setPendingGoal(null);
            }}
            onCancel={() => {
              setConfirmOpen(false);
              setPendingGoal(null);
            }}
          />
        </div>
      </section>

      {/* Beredskapstest */}
      <section className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-3 mb-6">
          <ClipboardCheck className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">{t('settings.prepTest.title')}</h2>
        </div>
        
        {isMemberReadOnly && (
          <div className="mb-4 p-3 rounded-lg bg-muted/50 border border-border">
            <p className="text-sm text-muted-foreground">
              Endast familjeägaren kan göra beredskapstestet. Resultatet visas för alla medlemmar på Översikt-sidan.
            </p>
          </div>
        )}
        
        {hasTest ? (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('settings.prepTest.lastResult')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.prepTest.score')}: <span className="font-semibold text-primary">{overallScore}%</span>
                  </p>
                  {testTakenAt && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('settings.prepTest.takenAt')}: {new Date(testTakenAt).toLocaleDateString('sv-SE')}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={async () => {
                if (!isMemberReadOnly) {
                  await resetTest();
                  setShowPrepTest(true);
                }
              }}
              disabled={isMemberReadOnly}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className="h-4 w-4" />
              {t('settings.prepTest.retake')}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('settings.prepTest.notTaken')}
            </p>
            <button
              onClick={() => {
                if (!isMemberReadOnly) {
                  setShowPrepTest(true);
                }
              }}
              disabled={isMemberReadOnly}
              className="flex items-center gap-2 btn-primary-custom disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ClipboardCheck className="h-4 w-4" />
              {t('settings.prepTest.takeNow')}
            </button>
          </div>
        )}
      </section>

      {/* Notifications */}
      <section className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-3 mb-6">
          <Bell className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">{t('settings.notifications.title')}</h2>
        </div>
        
        {/* Permission status banner - only show in web browser, not in mobile app */}
        {!appWindow.ReactNativeWebView && (
          <>
            {!permissionStatus.granted && (
              <div className="mb-4 p-4 rounded-lg bg-primary/10 border border-primary/20">
                <div className="flex items-start gap-3">
                  <Bell className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-medium mb-1">
                      Notifikationer inte aktiverade
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      För att få notifikationer i webbläsaren måste du först ge tillåtelse.
                    </p>
                    <button
                      onClick={handleEnableNotifications}
                      disabled={isRequestingPermission || !permissionStatus.canAskAgain}
                      className="px-4 py-2 bg-[#945123] hover:bg-[#7a3f1c] disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      {isRequestingPermission ? 'Aktiverar...' : 'Aktivera notifikationer'}
                    </button>
                    {!permissionStatus.canAskAgain && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Om knappen inte fungerar, kontrollera webbläsarens inställningar för notifikationer.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {permissionStatus.granted && (
              <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                <span className="text-sm text-green-800 dark:text-green-200">
                  Notifikationer är aktiverade i din webbläsare
                </span>
              </div>
            )}
          </>
        )}
        
        <div className="space-y-4">
          <SettingToggle 
            label={t('settings.notifications.vmaAlerts')}
            description={t('settings.notifications.vmaAlertsDesc')}
            checked={notifications.vmaAlerts}
            onChange={(v) => handleNotificationChange('vmaAlerts', v)}
          />
          <SettingToggle 
            label={t('settings.notifications.expirationWarnings')}
            description={t('settings.notifications.expirationWarningsDesc')}
            checked={notifications.expirationWarnings}
            onChange={(v) => handleNotificationChange('expirationWarnings', v)}
          />
          <SettingToggle 
            label={t('settings.notifications.yearWheelReminders')}
            description={t('settings.notifications.yearWheelRemindersDesc')}
            checked={notifications.yearWheelReminders}
            onChange={(v) => handleNotificationChange('yearWheelReminders', v)}
          />
          <SettingToggle 
            label={t('settings.notifications.waterReminders')}
            description={t('settings.notifications.waterRemindersDesc')}
            checked={notifications.waterReminders}
            onChange={(v) => handleNotificationChange('waterReminders', v)}
          />
          <SettingToggle 
            label={t('settings.notifications.familyChat')}
            description={t('settings.notifications.familyChatDesc')}
            checked={notifications.familyChat}
            onChange={(v) => handleNotificationChange('familyChat', v)}
          />
          <SettingToggle 
            label={t('settings.notifications.groupMembershipRequests')}
            description={t('settings.notifications.groupMembershipRequestsDesc')}
            checked={notifications.groupMembershipRequests}
            onChange={(v) => handleNotificationChange('groupMembershipRequests', v)}
          />
          <SettingToggle 
            label={t('settings.notifications.groupMembershipDecisions')}
            description={t('settings.notifications.groupMembershipDecisionsDesc')}
            checked={notifications.groupMembershipDecisions}
            onChange={(v) => handleNotificationChange('groupMembershipDecisions', v)}
          />
          <SettingToggle 
            label={t('settings.notifications.groupInvitations')}
            description={t('settings.notifications.groupInvitationsDesc')}
            checked={notifications.groupInvitations}
            onChange={(v) => handleNotificationChange('groupInvitations', v)}
          />
          <SettingToggle 
            label={t('settings.notifications.groupActivity')}
            description={t('settings.notifications.groupActivityDesc')}
            checked={notifications.groupActivity}
            onChange={(v) => handleNotificationChange('groupActivity', v)}
          />
          <SettingToggle 
            label={t('settings.notifications.groupThreadReplies')}
            description={t('settings.notifications.groupThreadRepliesDesc')}
            checked={notifications.groupThreadReplies}
            onChange={(v) => handleNotificationChange('groupThreadReplies', v)}
          />
        </div>
      </section>

      {/* VMA Region */}
      <section id="vma-region" className="rounded-xl border border-border bg-card p-6 scroll-mt-6">
        <div className="flex items-center gap-3 mb-6">
          <MapPin className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">{t('settings.vmaRegion.title')}</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          {t('settings.vmaRegion.description')}
        </p>
        
        {/* Postnummer input */}
        <div className="mb-4">
          <label className="text-sm font-medium mb-2 block">{t('settings.vmaRegion.postalCode')}</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={postalCode}
            onChange={(e) => {
              const numericValue = e.target.value.replace(/[^0-9]/g, '');
              handlePostalCodeChange(numericValue);
            }}
            placeholder={t('settings.vmaRegion.postalCodePlaceholder')}
            maxLength={6}
            className="w-full rounded-lg border border-border bg-background px-3 py-2"
          />
          {postalCodeError && (
            <p className="text-sm text-red-400 mt-1">{postalCodeError}</p>
          )}
        </div>
        
        {/* Region dropdown */}
        <div>
          <label className="text-sm font-medium mb-2 block">{t('settings.vmaRegion.selectCounty')}</label>
          <select 
            value={vmaRegion}
            onChange={(e) => handleRegionChange(e.target.value)}
            className="w-full rounded-lg border border-border bg-background px-3 py-2"
          >
            <option value="">Hela Sverige</option>
            {SWEDISH_REGIONS.map((region) => (
              <option key={region.code} value={region.code}>
                {region.name}
              </option>
            ))}
          </select>
        </div>
        
        {vmaRegion && (
          <div className="mt-4 p-3 rounded-lg bg-primary/10 border border-primary/20">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-primary" />
              <span className="text-sm">
                {t('settings.vmaRegion.selected')}: <strong>{SWEDISH_REGIONS.find(r => r.code === vmaRegion)?.name}</strong>
              </span>
            </div>
          </div>
        )}
      </section>

      {/* Language */}
      <section className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-3 mb-6">
          <Globe className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">{t('settings.language.title')}</h2>
        </div>
        <select 
          value={language}
          onChange={(e) => handleLanguageChange(e.target.value)}
          disabled
          className="w-full rounded-lg border border-border bg-background px-3 py-2 opacity-50 cursor-not-allowed"
        >
          <option value="sv">{t('settings.language.sv')}</option>
          <option value="en">{t('settings.language.en')}</option>
        </select>
      </section>

      {/* Account (removed - replaced by Premium section above) */}

      {/* Debug section (only in dev/staging) */}
      {(window.location.hostname === 'localhost' || 
        window.location.hostname === 'dev.beredskapshubben.se' || 
        window.location.hostname === 'staging.beredskapshubben.se') && (
        <section className="rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-6">
          <h2 className="text-lg font-semibold text-yellow-400 mb-4">🔧 Debug</h2>
          <div className="space-y-3">
            <button 
              onClick={() => navigate('/app/admin/notification-test')}
              className="w-full flex items-center justify-center gap-2 rounded-lg border border-yellow-500/50 px-4 py-2 text-yellow-400 hover:bg-yellow-500/10 transition-colors"
            >
              <Bell className="h-4 w-4" />
              Testa notifieringar
            </button>
          </div>
        </section>
      )}

      {/* Danger zone */}
      <section className="rounded-xl border border-red-500/30 bg-red-500/5 p-6">
        <h2 className="text-lg font-semibold text-red-400 mb-4">{t('settings.dangerZone.title')}</h2>
        <div className="space-y-3">
          <button 
            onClick={() => setShowLogoutModal(true)}
            className="w-full flex items-center justify-center gap-2 rounded-lg border border-border px-4 py-2 hover:bg-muted transition-colors"
          >
            <LogOut className="h-4 w-4" />
            {t('settings.dangerZone.logout')}
          </button>
          <button
            onClick={() => setConfirmDeleteOpen(true)}
            disabled={user?.subscriptionTier === 'premium'}
            aria-disabled={user?.subscriptionTier === 'premium'}
            className="w-full flex items-center justify-center gap-2 rounded-lg border border-red-500/50 px-4 py-2 text-red-400 transition-colors hover:bg-red-500/10 disabled:cursor-not-allowed disabled:border-red-500/20 disabled:text-red-300/50 disabled:hover:bg-transparent"
          >
            <Trash2 className="h-4 w-4" />
            {t('settings.account.deleteAccount')}
          </button>
          {user?.subscriptionTier === 'premium' && (
            <p className="text-sm text-red-200/80">
              {canOpenPaddlePortal ? (
                <>
                  {t('settings.account.deleteBlockedPrefix', 'Du kan inte radera kontot så länge Premium är aktivt. ')}
                  <button
                    type="button"
                    onClick={openPaddlePortal}
                    className="font-medium underline underline-offset-2 hover:text-red-100 transition-colors"
                  >
                    {t('settings.account.deleteBlockedAction', 'Avsluta din prenumeration')}
                  </button>
                  {t('settings.account.deleteBlockedSuffix', ' först.')}
                  {t('settings.account.deleteBlockedContactPrefix', ' Kontakta ')}
                  <button
                    type="button"
                    onClick={() => navigate('/app/support')}
                    className="font-medium underline underline-offset-2 hover:text-red-100 transition-colors"
                  >
                    {t('settings.account.deleteBlockedSupportLink', 'support')}
                  </button>
                  {t('settings.account.deleteBlockedContactSuffix', '.')}
                </>
              ) : subscriptionStoreType === 'google' ? (
                <>
                  {t('settings.account.deleteBlockedPrefix', 'Du kan inte radera kontot så länge Premium är aktivt. ')}
                  <button
                    type="button"
                    onClick={() => setPremiumManagementInfoStore('google')}
                    className="font-medium underline underline-offset-2 hover:text-red-100 transition-colors"
                  >
                    {t('settings.premium.manageInGooglePlay', 'Hantera i Google Play')}
                  </button>
                  {t('settings.account.deleteBlockedSuffix', ' först.')}
                </>
              ) : subscriptionStoreType === 'apple' ? (
                <>
                  {t('settings.account.deleteBlockedPrefix', 'Du kan inte radera kontot så länge Premium är aktivt. ')}
                  <button
                    type="button"
                    onClick={() => setPremiumManagementInfoStore('apple')}
                    className="font-medium underline underline-offset-2 hover:text-red-100 transition-colors"
                  >
                    {t('settings.premium.manageInAppStore', 'Hantera i App Store')}
                  </button>
                  {t('settings.account.deleteBlockedSuffix', ' först.')}
                </>
              ) : subscriptionStoreType === 'web' ? (
                <>
                  {t('settings.account.deleteBlockedPrefix', 'Du kan inte radera kontot så länge Premium är aktivt. ')}
                  <button
                    type="button"
                    onClick={() => setPremiumManagementInfoStore('web')}
                    className="font-medium underline underline-offset-2 hover:text-red-100 transition-colors"
                  >
                    {t('settings.premium.manageOnWeb', 'Hantera på webben')}
                  </button>
                  {t('settings.account.deleteBlockedSuffix', ' först.')}
                </>
              ) : (
                <>
                  {t('settings.account.deleteBlockedNoPortal', 'Du kan inte radera kontot så länge Premium är aktivt. Avsluta din prenumeration först. Kontakta ')}
                  <button
                    type="button"
                    onClick={() => navigate('/app/support')}
                    className="font-medium underline underline-offset-2 hover:text-red-100 transition-colors"
                  >
                    {t('settings.account.deleteBlockedSupportLink', 'support')}
                  </button>
                  {t('settings.account.deleteBlockedContactSuffix', '.')}
                </>
              )}
            </p>
          )}
          <AccountDeleteConfirmation open={confirmDeleteOpen} onConfirm={() => { setConfirmDeleteOpen(false); handleDeleteAccount(); }} onCancel={() => setConfirmDeleteOpen(false)} />
        </div>
      </section>

      {/* Confirm Disable Premium Modal */}
      <ConfirmationModal
        open={confirmDisablePremiumOpen}
        danger={true}
        title="Avsluta Premium"
        description={
          family && isFamilyOwner
            ? "Om du avslutar Premium kommer samtliga Premium-funktioner att inaktiveras, så som:\n\n• Årshjul för återkommande underhåll\n• Beredskapsplanering för 30 och 90 dagar\n• Familjeläget (alla familjemedlemmar kopplas bort)\n\nÄr du säker på att du vill fortsätta?"
            : "Om du avslutar Premium kommer samtliga Premium-funktioner att inaktiveras, så som:\n\n• Årshjul för återkommande underhåll\n• Beredskapsplanering för 30 och 90 dagar\n\nDitt beredskapsmål återställs till 7 dagar. Är du säker på att du vill fortsätta?"
        }
        confirmLabel="Ja, avsluta Premium"
        cancelLabel="Avbryt"
        onConfirm={async () => {
          const ok = await disablePremium();
          if (ok) {
            pushToast({
              title: 'Premium avslutat',
              description: 'Premium-funktioner är nu inaktiverade',
              variant: 'success',
            });
            // Refresh user data and navigate to dashboard
            await fetchUser();
            navigate('/');
          }
          setConfirmDisablePremiumOpen(false);
        }}
        onCancel={() => setConfirmDisablePremiumOpen(false)}
      />

      <ConfirmationModal
        open={premiumManagementInfoStore !== null}
        title={premiumManagementInfoTitle}
        description={premiumManagementInfoDescription}
        confirmLabel="Okej"
        cancelLabel="Stäng"
        onConfirm={() => setPremiumManagementInfoStore(null)}
        onCancel={() => setPremiumManagementInfoStore(null)}
      />

      {/* Export confirmation modal */}
      <ConfirmationModal
        open={showExportConfirm}
        title="Exportera beredskapsdata"
        description="Vill du exportera din beredskapsdata?"
        confirmLabel="Ja, exportera"
        cancelLabel="Avbryt"
        onConfirm={() => {
          setShowExportConfirm(false);
          handleExport();
        }}
        onCancel={() => setShowExportConfirm(false)}
      />

      {/* Import confirmation modal */}
      <ConfirmationModal
        open={showImportConfirm}
        danger={true}
        title="Importera beredskapsdata"
        description="Är du säker på att du vill importera beredskapsdata? Detta kommer att skriva över din befintliga beredskapsdata och åtgärden går inte att ångra."
        confirmLabel="Ja, importera"
        cancelLabel="Avbryt"
        onConfirm={() => {
          setShowImportConfirm(false);
          const pendingFile = appWindow.__pendingImportFile;
          if (pendingFile) {
            handleImport(pendingFile);
            appWindow.__pendingImportFile = undefined;
          }
        }}
        onCancel={() => {
          setShowImportConfirm(false);
          appWindow.__pendingImportFile = undefined;
        }}
      />

      {premiumActivationStatus !== 'idle' && (
        <ModalBackdrop>
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-xl bg-card border border-border shadow-xl overflow-hidden"
          >
            <div className="p-8 flex flex-col items-center text-center space-y-4">
              {premiumActivationStatus === 'polling' && (
                <>
                  <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
                  <h2 className="text-xl font-bold">Aktiverar din prenumeration…</h2>
                  <p className="text-muted-foreground text-sm">
                    Vänta ett ögonblick medan vi bekräftar din betalning.
                  </p>
                </>
              )}
              {premiumActivationStatus === 'success' && (
                <>
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/20">
                    <Crown className="h-8 w-8 text-amber-500 dark:text-amber-400" />
                  </div>
                  <h2 className="text-2xl font-bold">Välkommen till Premium!</h2>
                  <p className="text-muted-foreground">
                    Din prenumeration är nu aktiv. Du har tillgång till alla Premium-funktioner.
                  </p>
                  <button
                    onClick={() => { setPremiumActivationStatus('idle'); window.location.href = '/app'; }}
                    className="w-full mt-2 px-6 py-3 rounded-lg btn-primary-custom text-base font-semibold"
                  >
                    Sätt igång
                  </button>
                </>
              )}
              {premiumActivationStatus === 'failed' && (
                <>
                  <div className="text-5xl">⚠️</div>
                  <h2 className="text-xl font-bold">Något gick fel</h2>
                  <p className="text-muted-foreground text-sm">
                    Din betalning kan ha genomförts, men vi kunde inte bekräfta den automatiskt.
                    Kontakta vår support så hjälper vi dig.
                  </p>
                  <a
                    href="mailto:support@beredskapshubben.se"
                    className="w-full mt-2 px-6 py-3 rounded-lg btn-primary-custom text-base font-semibold text-center block"
                  >
                    Kontakta support
                  </a>
                  <button
                    onClick={() => setPremiumActivationStatus('idle')}
                    className="text-sm text-muted-foreground underline"
                  >
                    Stäng
                  </button>
                </>
              )}
            </div>
          </div>
        </ModalBackdrop>
      )}

      {showPremiumInfoModal && (
        <ModalBackdrop onClick={() => setShowPremiumInfoModal(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-xl bg-card border border-border shadow-xl overflow-hidden"
          >
            <div className="border-b border-border p-4">
              <h3 className="text-lg font-semibold">{t('settings.premium.launchInfo.title', 'Information')}</h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground whitespace-pre-line">
                {t(
                  'settings.premium.launchInfo.message',
                  'Beredskapshubben lanseras officiellt i April 2026.\n\nPremium-prenumeration via Google Play Store, Apple AppStore och web kommer inom kort!'
                )}
              </p>
              <button
                onClick={() => setShowPremiumInfoModal(false)}
                className="w-full px-4 py-2 rounded-lg btn-primary-custom"
              >
                {t('settings.premium.launchInfo.ok', 'Okej')}
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}


      {/* PrepTest Modal */}
      {showPrepTest && (
        <PrepTestModal 
          onClose={() => setShowPrepTest(false)} 
          isFirstTime={!hasTest}
        />
      )}

      {/* Logout Confirmation Modal */}
      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onConfirm={() => {
          setShowLogoutModal(false);
          logout();
        }}
        onCancel={() => setShowLogoutModal(false)}
      />

      {/* Premium Required Modal */}
      <PremiumRequiredModal
        open={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        feature={premiumFeature}
      />
    </div>
  );
}

// Confirmation modal for account deletion
function AccountDeleteConfirmation({ open, onConfirm, onCancel }: { open: boolean; onConfirm: () => void; onCancel: () => void }) {
  const { t } = useTranslation();
  return (
    <ConfirmationModal
      open={open}
      danger={true}
      title={t('settings.account.deleteConfirmTitle', 'Radera konto')}
      description={t('settings.account.deleteConfirmDesc', 'Detta kommer att radera all din data, hushållets data och familjeläget. Detta går inte att ångra.')}
      confirmLabel={t('settings.account.deleteConfirm', 'Radera konto')}
      cancelLabel={t('common.cancel', 'Avbryt')}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

interface SettingToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function SettingToggle({ label, description, checked, onChange }: SettingToggleProps) {
  return (
    <label className="flex items-start justify-between gap-4 cursor-pointer">
      <div className="flex-1">
        <div className="font-medium">{label}</div>
        {description && (
          <div className="text-sm text-muted-foreground">{description}</div>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative shrink-0 w-12 h-6 rounded-full transition-colors`}
        style={{ backgroundColor: checked ? '#67724e' : undefined }}
      >
        <span
          className={`absolute top-1 left-1 w-4 h-4 rounded-full ${checked ? 'bg-white' : 'bg-muted-foreground'} shadow-sm transition-transform duration-200 ${
            checked ? 'translate-x-6' : 'translate-x-0'
          }`}
        />
      </button>
    </label>
  );
}
